import { router } from '@inertiajs/react'
import { Calendar, MapPin, ArrowRight } from 'lucide-react'
import { VerifiedBadge } from '@/components/VerifiedBadge'
import { Badge } from '@/components/ui/badge'
import { formatDate, formatPrice } from '@/lib/format'
import type { EventListItem } from '@/types'

const PLACEHOLDER_COLORS = [
  'bg-primary/8',
  'bg-primary/5',
  'bg-zinc-100 dark:bg-zinc-800',
  'bg-secondary/15',
  'bg-secondary/10',
  'bg-zinc-50 dark:bg-zinc-800/50',
]

function hashIndex(id: string, len: number): number {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0
  }
  return Math.abs(hash) % len
}

type EventCardProps = {
  event: EventListItem
  showOrganization?: boolean
  priority?: boolean
}

export default function EventCard({ event, showOrganization = true, priority = false }: EventCardProps) {
  const hasImage = !!event.cover_image_url
  const primaryCause = event.organization ? event.organization.primary_cause : event.primary_cause

  return (
    <div className="group h-full overflow-hidden rounded-2xl bg-card shadow-sm ring-1 ring-black/5 transition-all duration-200 hover:ring-primary/20 hover:shadow-lg">
      <div className="relative h-44 overflow-hidden">
        {hasImage ? (
          <img
            src={event.cover_image_url!}
            alt=""
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            {...(priority ? { fetchPriority: 'high' as const } : {})}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className={`h-full w-full ${PLACEHOLDER_COLORS[hashIndex(event.id, PLACEHOLDER_COLORS.length)]}`}>
            <div className="flex h-full items-center justify-center">
              <Calendar className="size-8 text-primary/20" />
            </div>
          </div>
        )}
        {hasImage && (
          <div className="absolute inset-x-0 bottom-0 h-24 bg-linear-to-t from-black/50 to-transparent" />
        )}
        {primaryCause && (
          <div className="absolute bottom-3 left-3">
            <Badge className="bg-white/90 text-foreground shadow-sm backdrop-blur-sm hover:bg-white/90">{primaryCause}</Badge>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-lg font-semibold leading-snug line-clamp-2 text-balance">{event.title}</h3>
        {showOrganization && event.organization && (
          <span
            role="link"
            tabIndex={0}
            className="mt-1 inline-flex items-center gap-1 cursor-pointer text-base font-medium text-primary hover:underline"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault()
              e.stopPropagation()
              router.visit(`/o/${event.organization!.slug}`)
            }}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                router.visit(`/o/${event.organization!.slug}`)
              }
            }}
          >
            {event.organization.name}
            {event.organization.verified && <VerifiedBadge className="size-3.5" />}
          </span>
        )}
        {event.description && (
          <p className="mt-1.5 text-sm leading-snug text-foreground line-clamp-3">{event.description}</p>
        )}
        <div className="mt-3 space-y-1">
          <div className="flex items-center gap-1.5 text-base text-muted-foreground">
            <Calendar className="size-3.5 shrink-0" /><span>{event.date ? formatDate(event.date) : 'Fundraiser'}</span>
          </div>
          {(event.venue_name || event.city || event.state) && (
            <div className="flex items-center gap-1.5 text-base text-muted-foreground">
              <MapPin className="size-3.5 shrink-0" /><span className="truncate">{[event.venue_name, event.city, event.state].filter(Boolean).join(', ')}</span>
            </div>
          )}
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-black/5 pt-3">
          {event.starting_ticket_price
            ? <span className="text-lg font-semibold text-foreground tabular-nums">{formatPrice(event.starting_ticket_price)}</span>
            : <span />}
          <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </div>
  )
}
